import ContactPersonItem   from "../Entities/ContactPersonItem";
import ContactGroupItem    from '../Entities/ContactGroupItem';
import ContactItem         from "../Entities/ContactItem";
import ContactsData        from '../Entities/ContactsData';

export {ContactsHTTPMapping as default};

/**
* Contacts Model
*
* Maps contacts data from any source to the corresponding entities
*
* @author      rsimoes <ruben-r-simoes@telecom.pt>
* @copyright   Copyright (C) Sapo. All rights reserved.
* @license     Private
* @since       7.0.0
*/
class ContactsHTTPMapping {
  /**
   * Maps the server contacts results list into a new contacts data entity
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {ContactsData} contacts       contacts data entity
   * @param {Object}       server_results server results data
   *
   * @return {void}
   */
  mapResults(contacts, server_results) {
    if (server_results.list) {
      this.mapResultsList(contacts, server_results);
      return;
    }

    if (server_results.add) {
      this.mapResultsAdd(contacts, server_results);
      return;
    }

    if (server_results.update) {
      this.mapResultsUpdate(contacts, server_results);
      return;
    }

    if (server_results.delete) {
      this.mapResultsDelete(contacts, server_results);
      return;
    }
  }

  /**
   * Maps the server frequent contacts results
   *
   * This is parsed individually because it uses a different and old api
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {ContactsData} contacts       contacts data entity
   * @param {Object}       server_results server results data
   *
   * @return {void}
   */
  mapResultsFrequent(contacts, server_results) {
    if (!server_results.frequent) {
      return;
    }
  
    contacts.frequent.reset();

    server_results.frequent.forEach((value) => {
      contacts.addFrequentByEmail(value);
    })
  }

  /**
   * Maps the server contacts results list into a new contacts data entity
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {ContactsData} contacts       contacts data entity
   * @param {Object}       server_results server results data
   *
   * @return {void}
   */
  mapResultsList(contacts, server_results) {
    contacts.reset();

    server_results.list.forEach((data) => {
      let item = contacts.createItem(data.isGroup ? 'group' : 'person');
      this.mapContact(item, data);
      contacts.addItem(item)
    })

    contacts.fetched = true;
    contacts.addMembersToGroups();
    contacts.resort();
  }

  /**
   * Maps the server contacts results and adds it to the given contact data entity
   *
   * Returns an object with added contacts mapped by id
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {ContactsData} contacts        contacts data entity
   * @param {Object}             server_results  server results data
   *
   * @return {void}
   */
  mapResultsAdd(contacts, server_results) {
    server_results.add.forEach((data) => {
      let item = contacts.createItem(data.isGroup ? 'group' : 'person');
      this.mapContact(item, data);
      contacts.addItem(item)
    })

    contacts.resort();
    contacts.addMembersToGroups();
  }

  /**
   * Removes all deleted contacts from contacts data
   *
   * Returns an object with removed contacts mapped by id
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {ContactsData} contacts        contacts data entity
   * @param {Object}             server_results  server results data
   *
   * @return {void}
   */
  mapResultsDelete(contacts, server_results) {
    server_results.delete.forEach((id) => {
      contacts.deleteItem(id);
    })

    contacts.resort();
    contacts.addMembersToGroups();
  }


  /**
   * Maps the server contacts results add and adds it to the given contact data entity
   *
   * Returns an object with added contacts mapped by id
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {ContactsData} contacts        contacts data entity
   * @param {Object}             server_results  server results data
   *
   * @return {Array<ContactItem>}
   */
  mapResultsUpdate(contacts, server_results) {
    server_results.update.forEach((data) => {
      let item = contacts.getItem(data.id);
      this.mapContact(item, data);
    });

    contacts.resort();
    contacts.addMembersToGroups();
  }

  /**
   * Determines which type of contact it is to perform the right mapping
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {ContactsData} ContactsData contacts data entity
   * @param {Object}       data         contact server data
   *
   * @return {ContactItem}
   */
  mapContact(contact, data) {
    return (contact.type === 'group')
      ? this.mapGroup(contact, data)
      : this.mapPerson(contact, data)
  }

  /**
   * Maps server data into a persons contact entity
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {ContactPersonItem} person contacts person entity
   * @param {Object}            data   person server data
   *
   * @return {void}
   */
  mapPerson(person, data) {
    person.id           = data.id;
    person.name         = data.lastname   || '';
    person.email        = data.email      || '';
    person.company      = data.company    || '';
    person.mobile_phone = data.cellPhone  || '';
    person.home_address = data.homeStreet || '';
    person.birth_date   = data.birthday   || '';
    person.notes        = data.notes      || '';
    person.temporary    = false;
    person.recipient    = (person.name)
                        ? '"' + person.name + '" ' + '<' + person.email + '>'
                        : person.email;
  }

  /**
   * Maps server data into a group contact entity
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {ContactGroupItem} group contacts greoup  entity
   * @param {Object}             data  person server data
   *
   * @return {void}
   */
  mapGroup(group, data) {
    group.id            = data.id;
    group.name          = data.lastname || '';
    group.temporary     = false;
    group.members_ids   = [];

    if (data.gIds) {
      group.members_ids = Object.keys(data.gIds);
    }
  }
}